// "Rebuild planning index": calls the admin-only `rebuildPlanningIndex` callable, which rebuilds
// `globals/planning_index` (what the v2 meal plan planner works on) from every published recipe and the master
// ingredients, and shows what it found. The index is kept current on recipe writes by the recipe trigger; a
// master-ingredient change (a new pack size, a flipped perishable flag) touches no recipe, so this is how it is picked up.

import { showDialog } from "./ui/dialog.js";

export const SKIP_REASON_LABELS = {
  sold_by_weight: "Sold by weight (counted as zero waste, by design)",
  no_pack_size: "Measured in a cup or spoon and the ingredient has no pack size",
  unit_not_convertible: "Recipe unit can't be converted to the ingredient's pack size",
  unit_mismatch: "Recipe unit doesn't match the ingredient's unit",
  no_pack_definition: "Ingredient has no canonical quantity",
  no_amount: "Recipe line has no amount",
  perishable_unknown: "Ingredient has no perishable flag",
  unknown_ingredient: "Ingredient is not in the master ingredients"
};

const PROBLEM_REASON_HINTS = {
  perishable_unknown: "set is_perishable",
  unknown_ingredient: "add it to master ingredients or fix the recipe"
};

function escapeHtml(value) {
  return String(value ?? "").replace(/[&<>"']/g, (ch) => (
    { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[ch]
  ));
}

export function formatBytes(bytes) {
  const size = Number(bytes) || 0;
  if (size < 1024) return `${size} B`;
  return `${(size / 1024).toFixed(size < 10 * 1024 ? 1 : 0)} KB`;
}

/** A one-line summary for the toast. */
export function summarizePlanningIndexResult(data) {
  const recipes = Number(data?.recipes) || 0;
  const withData = Number(data?.with_waste_data) || 0;
  return `Planning index rebuilt: ${recipes} recipe${recipes === 1 ? "" : "s"}, ${withData} with waste data`;
}

function statRow(label, value) {
  return `<tr><td class="py-1.5 text-gray-500 dark:text-gray-400">${escapeHtml(label)}</td>` +
    `<td class="py-1.5 text-right font-semibold text-gray-900 dark:text-white">${escapeHtml(value)}</td></tr>`;
}

/** The result panel: counts, the lines left out by reason, and the ingredients whose data is missing. */
export function renderPlanningIndexResult(data) {
  const skipped = Object.entries(data?.skipped_by_reason || {}).sort((a, b) => b[1] - a[1]);
  const problems = Array.isArray(data?.problem_ingredients) ? data.problem_ingredients : [];
  const seconds = ((Number(data?.duration_ms) || 0) / 1000).toFixed(1);

  const counts = [
    statRow("Recipes indexed", data?.recipes ?? 0),
    statRow("With waste data", data?.with_waste_data ?? 0),
    statRow("No perishable ingredients (known empty)", data?.known_empty ?? 0),
    statRow("Unknown (perishable flags missing)", data?.unknown ?? 0),
    statRow("Document size", formatBytes(data?.bytes)),
    statRow("Took", `${seconds} s`)
  ].join("");

  const skippedHtml = skipped.length === 0
    ? `<p class="text-gray-500 dark:text-gray-400">Every perishable line was counted.</p>`
    : `<ul class="list-disc space-y-1 pl-5">${skipped.map(([reason, count]) =>
      `<li><strong>${escapeHtml(count)}</strong> ${escapeHtml(SKIP_REASON_LABELS[reason] || reason)}</li>`).join("")}</ul>`;

  const problemsHtml = problems.length === 0
    ? ""
    : `<h4 class="mt-5 mb-1.5 font-semibold text-gray-900 dark:text-white">Ingredients to fix</h4>
       <ul class="list-disc space-y-1 pl-5">${problems.map((entry) =>
      `<li><code class="font-mono text-xs">${escapeHtml(entry.ingredient_id)}</code> in ${escapeHtml(entry.recipes)} recipe${entry.recipes === 1 ? "" : "s"}: ` +
      `${escapeHtml(PROBLEM_REASON_HINTS[entry.reason] || entry.reason)}</li>`).join("")}</ul>`;

  return `<table class="w-full">${counts}</table>
    <h4 class="mt-5 mb-1.5 font-semibold text-gray-900 dark:text-white">Lines left out of the waste count</h4>
    ${skippedHtml}
    ${problemsHtml}`;
}

/**
 * @param {{functions: object, httpsCallable: Function, showToast: Function}} deps
 */
export async function rebuildPlanningIndex({ functions, httpsCallable, showToast }) {
  const button = document.getElementById("btnRebuildPlanningIndex");
  const label = button ? button.innerHTML : "";
  if (button) {
    button.disabled = true;
    button.textContent = "Rebuilding…";
  }

  try {
    const callable = httpsCallable(functions, "rebuildPlanningIndex");
    const result = await callable();
    const data = result?.data || {};
    if (!data.success) {
      showToast("The planning index rebuild did not complete.", "error");
      return;
    }
    showToast(summarizePlanningIndexResult(data), "success");
    const body = document.getElementById("planningIndexResultBody");
    if (body) body.innerHTML = renderPlanningIndexResult(data);
    showDialog("planningIndexModal");
  } catch (err) {
    console.error("Rebuild planning index error:", err);
    showToast(`Rebuild failed: ${err.message}`, "error");
  } finally {
    if (button) {
      button.disabled = false;
      button.innerHTML = label;
    }
  }
}
